/**
 * useRegisterTimelog
 * Layer: presentation hook (wires application use case to React components)
 *
 * Instantiates RegisterTimelogUseCase with the CompositeTimelogRepository
 * and LocalStorageDeviceIdProvider from the infrastructure container.
 * Pages use this hook instead of importing repositories or providers directly.
 *
 * Design: correcto-registro-de-timelogs-locales-y-remotos (D1, D3)
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RegisterTimelogUseCase } from '../../application/use-cases/RegisterTimelogUseCase';
import type { RegisterTimelogInput } from '../../application/use-cases/RegisterTimelogUseCase';
import type { Timelog } from '../../domain/entities/Timelog';
import { getTimelogRepository } from '../../infrastructure/repositories/container';
import { LocalStorageDeviceIdProvider } from '../../infrastructure/providers/LocalStorageDeviceIdProvider';
import { timelogKeys } from '../../application/timelogKeys';

let deviceProvider: LocalStorageDeviceIdProvider | null = null;
function getDeviceProvider(): LocalStorageDeviceIdProvider {
  deviceProvider ??= new LocalStorageDeviceIdProvider();
  return deviceProvider;
}

export interface UseRegisterTimelogResult {
  execute: (
    input: RegisterTimelogInput,
  ) => Promise<Timelog | { virtualSuccess: true; localId: string }>;
  isPending: boolean;
  isSuccess: boolean;
  reset: () => void;
}

export function useRegisterTimelog(): UseRegisterTimelogResult {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const reset = useCallback(() => {
    setIsPending(false);
    setIsSuccess(false);
  }, []);

  const execute = useCallback(
    async (input: RegisterTimelogInput) => {
      setIsPending(true);
      setIsSuccess(false);
      try {
        const useCase = new RegisterTimelogUseCase(
          getTimelogRepository(),
          getDeviceProvider(),
        );
        const result = await useCase.execute(input);
        setIsSuccess(true);
        
        // Fix 1: Invalidate timelog queries so the new mark appears immediately in lists
        try {
          await queryClient.invalidateQueries({ queryKey: timelogKeys.all });
        } catch (err) {
          console.warn('[useRegisterTimelog] Failed to invalidate queries:', err);
        }
        
        return result;
      } finally {
        setIsPending(false);
      }
    },
    [queryClient],
  );

  return { execute, isPending, isSuccess, reset };
}
